import React, { useState } from "react";
import { useComponentDidMount } from "../../../lib/hook";
import { Col, ControlLabel, FormControl, FormGroup, Glyphicon, Grid, Row } from "react-bootstrap";
import "./audio_block_modal.scss";
import { If } from "react-extras";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import get from "lodash/get";
import { getItemStyle, AudioTypes, NewAudioTypes } from "./audio_block_modal_helpers";
import FileUploader from "../../lib/file_uploader";
import { connect } from "react-redux";
import { MediaFileActionManager } from "../../../action_managers/shows";
import InfoTooltip from "../../lib/info";
import ExternalLink from "../../lib/external_link";

export const getListStyle = () => ({
  padding: "16px 40px",
  width: "100%",
});

const BlockEditor = ({
  playStyle,
  onPlaystyleChange,
  limit,
  onLimitChange,
  onAddAudio,
  disabled,
  ...rest
}) => {
  return (
    <Grid fluid>
      <Row>
        <Col xs={12} md={8} mdOffset={2}>
          <p>Create the parts of your audio block, and order them as you see fit.</p>
          <div className={"audio-block-picker"}>
            <div style={getListStyle()}>
              <Row>
                <Col xs={12} md={6}>
                  <FormGroup
                    controlId="formControlsSelect"
                    className={"flex-row-container align-center no-margin"}>
                    <ControlLabel
                      style={{ width: 110, margin: 0, fontSize: 11 }}
                      className={"semi-bold"}>
                      PLAY STYLE:
                    </ControlLabel>
                    <FormControl
                      disabled={disabled}
                      value={playStyle}
                      onChange={onPlaystyleChange}
                      style={{ height: 28 }}
                      componentClass="select"
                      className={"margin-bottom--none"}
                      placeholder="select">
                      {/*  disabled until we figure out what this means <option value="priority">Priority</option>*/}
                      <option value="inorder">In Order</option>
                      <option value="random">Random</option>
                    </FormControl>
                  </FormGroup>
                </Col>
                <If condition={playStyle === "inorder" || playStyle === "random"}>
                  <Col xs={12} md={6}>
                    <FormGroup
                      controlId="formControlsSelect"
                      className={"flex-row-container align-center no-margin"}>
                      <ControlLabel
                        style={{ width: 110, margin: 0, fontSize: 11 }}
                        className={"semi-bold"}>
                        PLAY UP TO:
                      </ControlLabel>
                      <FormControl
                        min="1"
                        disabled={disabled}
                        value={limit}
                        onChange={e => {
                          const value = parseInt(e.target.value);
                          if (value < 1) return;
                          onLimitChange({
                            target: { value },
                          });
                        }}
                        style={{ height: 28 }}
                        componentClass="input"
                        type={"number"}
                        className={"margin-bottom--none"}
                      />
                    </FormGroup>
                  </Col>
                </If>
              </Row>
            </div>
            <DragAndDrop {...rest} disabled={disabled} />
            <If condition={!disabled}>
              <AddAudioButton onSelect={onAddAudio} />
            </If>
          </div>
        </Col>
      </Row>
    </Grid>
  );
};

const renderUploader = (onClick, file, { mediaFileUUID, mediaFiles }) => {
  let existingFileName;
  if (mediaFileUUID) {
    existingFileName = "..." + get(mediaFiles, [mediaFileUUID, "convertedURL"], "").slice(-22);
  }
  return (
    <>
      <span className="fake-link" onClick={onClick}>
        {mediaFileUUID || file ? get(file, "name") || existingFileName : "Choose MP3 "}
      </span>
    </>
  );
};

const DragAndDrop = ({
  items,
  updateItems,
  removeItem,
  onFileLoad,
  mediaFiles,
  getMediaFile,
  disabled,
}) => {
  const onDragEnd = result => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }
    const newItems = reorder(items, result.source.index, result.destination.index);
    updateItems(newItems);
  };
  useComponentDidMount(() => {
    items
      .filter(({ mediaFileUUID }) => mediaFileUUID && !mediaFiles[mediaFileUUID])
      .forEach(({ mediaFileUUID }) => {
        getMediaFile(mediaFileUUID);
      });
  }, []);

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="droppable">
        {(provided, snapshot) => (
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            style={getListStyle(snapshot.isDraggingOver)}>
            {items.map((item, index) => {
              return (
                <Draggable
                  key={item.id}
                  draggableId={item.id}
                  index={index}
                  isDragDisabled={disabled}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}>
                      <Glyphicon glyph={"align-justify"} className={"drag-icon"} />
                      <span className="draggable-text">
                        <span className={"bold"}>{get(AudioTypes, [item.type, "display"])}</span>
                        <If condition={!!get(AudioTypes, [item.type, "secondaryDisplay"], false)}>
                          <span>{get(AudioTypes, [item.type, "secondaryDisplay"])}</span>
                        </If>
                        <If condition={!!get(AudioTypes, [item.type, "toolTipText"], false)}>
                          <InfoTooltip helpText={get(AudioTypes, [item.type, "toolTipText"])} />
                        </If>
                        <If condition={get(AudioTypes, [item.type, "showUploader"], false)}>
                          <FileUploader
                            file={item.file}
                            mediaFileUUID={item.mediaFileUUID}
                            mediaFiles={mediaFiles}
                            Clickable={renderUploader}
                            id={item.id}
                            onFileLoad={onFileLoad}
                          />
                          <ExternalLink
                            href={get(mediaFiles, [item.mediaFileUUID, "convertedURL"])}>
                            <Glyphicon
                              style={{ margin: "2px 0 0 6px", color: "gray", cursor: "pointer" }}
                              glyph="new-window"
                            />
                          </ExternalLink>
                        </If>
                        <span className="flex-1" />
                        <If condition={!disabled}>
                          <Glyphicon
                            glyph={"remove"}
                            className={"remove-button"}
                            onClick={e => removeItem(item.id, e)}
                          />
                        </If>
                      </span>
                    </div>
                  )}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

// a little function to help us with reordering the result
export const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

export const AddAudioButton = ({ onSelect }) => {
  const [open, setOpen] = useState(false);
  return (
    <Row className={"add-audio-button"}>
      <Col xs={12} md={12}>
        <div className={"flex-row-container justify-center width-100"}>
          <div className={"background-divider"} />

          <button
            className="btn btn-secondary button"
            onClick={() => {
              setOpen(true);
            }}>
            Add Audio
            <If condition={open}>
              <div className={"add-block-options"}>
                <div className={"add-audio-block-select"}>
                  {NewAudioTypes.map(({ type, display }) => (
                    <div
                      key={type}
                      className="options"
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        setOpen(false);
                        onSelect(type);
                      }}>
                      {display}
                    </div>
                  ))}
                </div>
              </div>
            </If>
          </button>
        </div>
      </Col>
    </Row>
  );
};
export default connect(
  state => ({
    mediaFiles: state.mediaFiles,
  }),
  {
    getMediaFile: mediaFileUUID =>
      new MediaFileActionManager({
        mediaFileUUID,
      }).run(),
  }
)(BlockEditor);
